import Pusher, { Channel } from 'pusher-js'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import getLogger from 'utils/log/log'
import { describeConnectionError, reportConnectionOutage, watchConnectionOutage } from './connectionFailureReporting'

const log = getLogger('PusherContext')

type ConnectionState = 'initialized' | 'connecting' | 'connected' | 'unavailable' | 'failed' | 'disconnected'

type PusherContextValue = {
  client: Pusher | null
  connectionState: ConnectionState
  subscribe: (name: string) => Channel | null
}

const PusherContext = createContext<PusherContextValue>({
  client: null,
  connectionState: 'disconnected',
  subscribe: () => null,
})

interface Props {
  clientKey?: string
  cluster?: string
  children: React.ReactNode
}

/**
 * Provides Pusher WebSocket connectivity to the application.
 *
 * Connection lifecycle:
 * 1. Creates Pusher client when `clientKey` is provided
 * 2. Tracks connection state: disconnected → connecting → connected
 * 3. Handles reconnection automatically (activityTimeout: 30s, pongTimeout: 10s)
 * 4. Logs all state changes and errors for debugging
 *
 * Context provides:
 * - `client`: The Pusher instance (or null if not connected)
 * - `connectionState`: Current state ('connected', 'connecting', 'disconnected', etc.)
 * - `subscribe`: Function to subscribe to a channel
 */
export function PusherProvider({ clientKey, cluster = 'eu', children }: Props) {
  const [client, setClient] = useState<Pusher | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')

  useEffect(() => {
    if (!clientKey) return

    const p = new Pusher(clientKey, {
      cluster,
      // Enable automatic reconnection with exponential backoff
      activityTimeout: 30000, // Send ping if no activity for 30s
      pongTimeout: 10000, // Wait 10s for pong response
    })

    // Track connection state changes
    p.connection.bind('state_change', (states: { previous: ConnectionState; current: ConnectionState }) => {
      log.info(`Pusher connection state changed: ${states.previous} -> ${states.current}`)
      setConnectionState(states.current)
    })

    p.connection.bind('connected', () => {
      log.info('Pusher connected successfully')
    })

    p.connection.bind('error', (error: unknown) => {
      log.warn(`Pusher connection error: ${describeConnectionError(error)}`)
    })

    const stopOutageWatch = watchConnectionOutage(p.connection, reportConnectionOutage)
    setClient(p)
    setConnectionState(p.connection.state as ConnectionState)

    return () => {
      stopOutageWatch()
      p.disconnect()
      setClient(null)
      setConnectionState('disconnected')
    }
  }, [clientKey, cluster])

  const subscribe = useCallback(
    (name: string) => {
      if (!client) return null
      if (!name) return null
      return client.subscribe(name)
    },
    [client]
  )

  const value = useMemo<PusherContextValue>(
    () => ({
      client,
      connectionState,
      subscribe,
    }),
    [client, connectionState, subscribe]
  )

  return <PusherContext.Provider value={value}>{children}</PusherContext.Provider>
}

/**
 * Access the Pusher context (client, connectionState, subscribe function).
 */
export function usePusher() {
  return useContext(PusherContext)
}

export type ChannelState = 'initialized' | 'subscribing' | 'succeeded' | 'failed'

export type UseChannelResult = {
  channel: Channel | null
  state: ChannelState
}

/**
 * Subscribe to a Pusher channel with automatic retry and reconnection handling.
 *
 * Subscription lifecycle:
 * 1. Waits for Pusher to be connected before subscribing
 * 2. Subscribes and tracks state: initialized → subscribing → succeeded/failed
 * 3. On failure: retries up to 3 times with exponential backoff (2s, 4s, 8s)
 * 4. On reconnect: automatically re-subscribes to the channel
 * 5. On unmount: cleans up subscriptions and pending retries
 *
 * @param name - Channel name to subscribe to (undefined to skip subscription)
 * @returns Object with `channel` (Channel instance or null) and `state` (subscription state)
 *
 * Note: Channel is returned during 'subscribing' state to allow early event binding.
 * Events bound while subscribing will fire once subscription succeeds.
 */
export function useChannel(name: string | undefined): UseChannelResult {
  const { subscribe, client, connectionState } = usePusher()
  const [channel, setChannel] = useState<Channel | null>(null)
  const [channelState, setChannelState] = useState<ChannelState>('initialized')
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const channelRef = useRef<Channel | null>(null)

  const MAX_RETRIES = 3
  const RETRY_DELAY_MS = 2000

  useEffect(() => {
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    if (!name || !client) {
      setChannel(null)
      channelRef.current = null
      setChannelState('initialized')
      retryCountRef.current = 0
      return
    }

    // Only subscribe when connected
    if (connectionState !== 'connected') {
      log.debugging(`Waiting for Pusher connection before subscribing to channel: ${name}`)
      return
    }

    const attemptSubscribe = (): void => {
      const ch = subscribe(name)
      if (!ch) {
        log.warn(`Failed to subscribe to channel: ${name}`)
        setChannelState('failed')
        return
      }

      setChannelState('subscribing')

      // Unbind any existing handlers to prevent accumulation during retries
      ch.unbind('pusher:subscription_succeeded')
      ch.unbind('pusher:subscription_error')

      // Handle subscription success
      ch.bind('pusher:subscription_succeeded', () => {
        log.info(`Successfully subscribed to channel: ${name}`)
        setChannelState('succeeded')
        retryCountRef.current = 0
      })

      // Handle subscription error with retry
      ch.bind('pusher:subscription_error', (error: { type: string; error: string; status?: number }) => {
        const statusText = error.status === undefined ? '' : ` (status ${error.status})`
        log.error(`Subscription error for channel ${name}: ${error.type}: ${error.error}${statusText}`)
        setChannelState('failed')

        // Retry with exponential backoff
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = RETRY_DELAY_MS * Math.pow(2, retryCountRef.current)
          retryCountRef.current++
          log.info(`Retrying subscription to ${name} in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`)

          retryTimeoutRef.current = setTimeout(() => {
            client.unsubscribe(name)
            attemptSubscribe()
          }, delay)
        } else {
          log.error(`Max retries reached for channel: ${name}`)
        }
      })

      channelRef.current = ch
      setChannel(ch)
    }

    attemptSubscribe()

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
      if (channelRef.current) {
        channelRef.current.unbind_all()
        channelRef.current = null
      }
      if (client) {
        client.unsubscribe(name)
      }
      setChannel(null)
      setChannelState('initialized')
      retryCountRef.current = 0
    }
  }, [name, subscribe, client, connectionState])

  // Return channel during subscribing/succeeded states, null otherwise
  // This allows event binding during subscription while preventing binding to failed channels
  const returnedChannel = channelState === 'succeeded' || channelState === 'subscribing' ? channel : null

  return { channel: returnedChannel, state: channelState }
}

type UseEventOptions = {
  /**
   * Add random delay before executing handler to prevent thundering herd.
   * Format: [minMs, maxMs] - e.g., [5000, 20000] for 5-20 second delay
   */
  randomDelay?: [number, number]
}

/**
 * Bind an event handler to a Pusher channel.
 *
 * Features:
 * - Automatically binds/unbinds when channel or eventName changes
 * - Catches and logs handler errors to prevent breaking other handlers
 * - Supports optional random delay to prevent thundering herd
 * - Cleans up pending timeouts on unmount
 *
 * How Pusher event binding works:
 * - `channel.bind(eventName, handler)` registers a callback for the event
 * - `channel.unbind(eventName, handler)` removes the specific callback (idempotent)
 * - Multiple handlers can be bound to the same event
 * - Handlers are called synchronously in registration order when event fires
 * - Pusher does NOT use handler return values, so async handlers are fire-and-forget
 *
 * Error handling:
 * - Handler errors are caught and logged to prevent breaking other handlers
 * - Pusher would otherwise silently swallow errors or break the event chain
 *
 * Random delay (thundering herd prevention):
 * - When many clients receive the same event, they may all make API calls simultaneously
 * - `randomDelay: [minMs, maxMs]` spreads out handler execution over a time window
 * - Each handler invocation gets a random delay within the specified range
 *
 * @param channel - Channel to bind to (from useChannel)
 * @param eventName - Event name to listen for
 * @param handler - Callback function (can be async)
 * @param options - Optional config (randomDelay: [minMs, maxMs])
 */
export function useEvent(
  channel: Channel | null | undefined,
  eventName: string,
  handler: (data: any) => void | Promise<void>,
  options?: UseEventOptions
) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const delayMin = options?.randomDelay?.[0]
  const delayMax = options?.randomDelay?.[1]

  useEffect(() => {
    if (!channel || !eventName) {
      return
    }

    const timeoutIds: NodeJS.Timeout[] = []

    // Note: Pusher doesn't use handler return values, so we don't need to return a Promise.
    // Errors are caught and logged internally to prevent breaking other handlers.
    const wrapped = (data: unknown): void => {
      const executeHandler = async (): Promise<void> => {
        try {
          await handlerRef.current(data)
        } catch (error) {
          log.error(`Error in Pusher event handler for "${eventName}":`, error)
        }
      }

      if (delayMin !== undefined && delayMax !== undefined) {
        const delay = Math.floor(Math.random() * (delayMax - delayMin)) + delayMin
        const timeoutId = setTimeout(() => {
          executeHandler()
        }, delay)
        timeoutIds.push(timeoutId)
      } else {
        executeHandler()
      }
    }

    // Bind the event
    channel.bind(eventName, wrapped)

    return () => {
      // Pusher's unbind is idempotent, safe to call unconditionally
      channel.unbind(eventName, wrapped)
      log.debugging(`Unbound event "${eventName}" from channel "${channel.name}"`)
      // Clear all pending timeouts to prevent handlers from being called after unmount
      timeoutIds.forEach((id) => clearTimeout(id))
    }
  }, [channel, eventName, delayMin, delayMax])
}

/**
 * Hook to access Pusher connection state for debugging or UI feedback
 */
export function usePusherConnectionState(): ConnectionState {
  const { connectionState } = usePusher()
  return connectionState
}

/**
 * Request headers identifying this client's Pusher socket. The backend excludes this
 * socket when broadcasting, so the originating client does not receive its own event.
 */
export function usePusherSocketIdHeaders(): Record<string, string> {
  const { client } = usePusher()
  const socketId = client?.connection.socket_id

  return socketId ? { 'Ws-Socket': socketId } : {}
}
